import {useMemo} from 'react'
import {FlatList, Pressable, View} from 'react-native'
import {type ChatBskyConvoDefs, RichText as RichTextAPI} from '@atproto/api'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {
  DEMO_CONVO_ID,
  DEMO_GROUP_MEMBER,
  demoInviteLink,
  demoPeer,
  resetDemoScenario,
  setPeerDeclined,
  useDemoFamily,
} from '#/lib/demoFamily'
import {
  buildFamilyDeclineMessage,
  buildFamilyInviteMessage,
  FAMILY_DECLINE_CAUSES,
} from '#/lib/familyCopy'
import {type CommonNavigatorParams} from '#/lib/routes/types'
import {familyInviteCodeFromFacets} from '#/state/queries/family'
import {useSession} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {MessageFamilyInvite} from '#/components/dms/MessageFamilyInvite'
import * as Layout from '#/components/Layout'
import {RichText} from '#/components/RichText'
import {Text} from '#/components/Typography'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'DemoConversation'>

// Fixed clock for the simulated timestamps, so the demo messages don't move
// around as the screen re-renders.
const DEMO_CLOCK_START = Date.now()

type DemoMessage = {
  key: string
  message: ChatBskyConvoDefs.MessageView
}

/**
 * The demo conversation: a simulated DM thread with the demo peer containing
 * the family invite message(s). The bubbles mirror the real chat's 1:1 visual
 * language (self = primary bubble on the right, peer = contrast bubble on the
 * left) and the invite is rendered by the exact same component the real chat
 * uses under the bubble (MessageFamilyInvite), so what you see is what the
 * real flow will look like.
 *
 * The real MessageItem component can't be reused here because it requires a
 * live ConvoProvider (chat agent + websocket), which doesn't exist for a
 * simulated thread — so the bubbles are a lightweight lookalike and the
 * invite embed stays the real component.
 */
export function DemoConversationScreen({route}: Props) {
  const t = useTheme()
  const {_} = useLingui()
  const demo = useDemoFamily()
  const {currentAccount} = useSession()

  // The demo supports a simulated family GROUP chat (demo peer + Emma).
  // Params can arrive as boolean (in-app) or string 'true' (web URL round-trip).
  const isGroup = !!route.params?.group

  const peer = useMemo(() => demoPeer(demo.role), [demo.role])
  const myDid = currentAccount?.did
  // The sender's own profile name goes into the message, so the recipient
  // always knows exactly who is asking ("Hi, I'm …").
  const myName = currentAccount?.handle ?? 'Taderok friend'

  const items = useMemo<DemoMessage[]>(() => {
    const items: DemoMessage[] = []
    const now = DEMO_CLOCK_START

    // 1) Our own invite (only when we generated a code and shared it). The
    //    embed shows the "waiting for them to accept" state, because it's our
    //    own code.
    if (demo.sentToChat && demo.code && myDid) {
      const myLink = demoInviteLink(demo.code)
      items.push({
        key: `${DEMO_CONVO_ID}-my-invite`,
        message: buildInviteMessage(
          buildFamilyInviteMessage({
            role: demo.role === 'learner' ? 'child' : 'parent',
            name: myName,
            link: myLink,
          }),
          myDid,
          `${DEMO_CONVO_ID}-msg-my`,
          new Date(now - 6 * 60_000).toISOString(),
        ),
      })
    }

    // 2) Sender-side simulation: the demo peer politely declines OUR
    //    invitation (mirrors the receiver's decline feedback message).
    if (demo.sentToChat && demo.code && demo.peerDeclinedCause && myDid) {
      items.push({
        key: `${DEMO_CONVO_ID}-peer-decline`,
        message: buildInviteMessage(
          buildFamilyDeclineMessage({
            name: peer.displayName,
            cause: demo.peerDeclinedCause,
          }),
          peer.did,
          `${DEMO_CONVO_ID}-msg-peer-decline`,
          new Date(now - 5 * 60_000).toISOString(),
        ),
      })
    }

    // 3) Group variant: a second member welcomes everyone (general, warm —
    //    no heavy family wording).
    if (isGroup) {
      const emmaText = "Hi! I'm Emma — so happy we're all here together 💛"
      items.push({
        key: `${DEMO_CONVO_ID}-emma-welcome`,
        message: buildInviteMessage(
          emmaText,
          DEMO_GROUP_MEMBER.did,
          `${DEMO_CONVO_ID}-msg-emma`,
          new Date(now - 4 * 60_000).toISOString(),
        ),
      })
    }

    // 3) The demo peer's invitation — the receiving side of the flow. While
    //    it's still open it carries the Accept button. Same message shape as
    //    our own side, just from the peer's profile.
    if (!demo.accepted) {
      const peerLink = demoInviteLink(`DEMO-${peer.did.slice(-6).toUpperCase()}`)
      items.push({
        key: `${DEMO_CONVO_ID}-peer-invite`,
        message: buildInviteMessage(
          buildFamilyInviteMessage({
            role: demo.role === 'learner' ? 'parent' : 'child',
            name: peer.displayName,
            link: peerLink,
          }),
          peer.did,
          `${DEMO_CONVO_ID}-msg-peer`,
          new Date(now - 2 * 60_000).toISOString(),
        ),
      })
    }

    // 4) WE declined the peer's invitation — our polite reply is sent back
    //    in the thread, exactly like the accept thanks message.
    if (demo.declinedReason && myDid) {
      items.push({
        key: `${DEMO_CONVO_ID}-our-decline`,
        message: buildInviteMessage(
          buildFamilyDeclineMessage({
            name: myName,
            cause: demo.declinedReason,
          }),
          myDid,
          `${DEMO_CONVO_ID}-msg-our-decline`,
          new Date(now - 90_000).toISOString(),
        ),
      })
    }

    // 5) Their reply once they've accepted. A real, warm message — a child
    //    talking to their dad, general rather than literal "family" wording.
    if (demo.accepted) {
      const thanksText =
        demo.role === 'learner'
          ? "You're welcome! I'll always be here for you, no matter what 💛"
          : "Thanks! I'm so happy you're here with me 💛"
      if (isGroup) {
        const emmaCongrats =
          "Yay! So happy you joined us 💛"
        items.push({
          key: `${DEMO_CONVO_ID}-emma-congrats`,
          message: buildInviteMessage(
            emmaCongrats,
            DEMO_GROUP_MEMBER.did,
            `${DEMO_CONVO_ID}-msg-emma-congrats`,
            new Date(now - 15_000).toISOString(),
          ),
        })
      }
      items.push({
        key: `${DEMO_CONVO_ID}-peer-thanks`,
        message: buildInviteMessage(
          thanksText,
          peer.did,
          `${DEMO_CONVO_ID}-msg-thanks`,
          new Date(now - 30_000).toISOString(),
        ),
      })
    }

    return items
  }, [
    demo.sentToChat,
    demo.code,
    demo.role,
    demo.accepted,
    demo.declinedReason,
    demo.peerDeclinedCause,
    myDid,
    myName,
    peer,
    isGroup,
  ])

  if (!demo.enabled) {
    return (
      <Layout.Screen>
        <Layout.Content centerContent>
          <View style={[a.py_4xl, a.px_xl, a.align_center, a.gap_lg]}>
            <Text style={[a.text_2xl, a.font_bold]}>
              <Trans>Demo mode is off</Trans>
            </Text>
            <Text style={[a.text_md, a.text_center]}>
              <Trans>
                Turn on Demo mode from Settings › Account › Family to open this
                simulated conversation.
              </Trans>
            </Text>
          </View>
        </Layout.Content>
      </Layout.Screen>
    )
  }

  return (
    <Layout.Screen minimalShell testID="demoConvoScreen">
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            {isGroup ? 'Family group' : peer.displayName}
          </Layout.Header.TitleText>
          {isGroup ? (
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              3 members
            </Text>
          ) : null}
        </Layout.Header.Content>
        <Layout.Header.Slot>
          <View
            style={[
              a.rounded_full,
              {
                backgroundColor: t.palette.primary_100,
                paddingHorizontal: 10,
                paddingVertical: 3,
              },
            ]}>
            <Text
              style={[a.text_xs, a.font_semi_bold, {color: t.palette.primary_900}]}>
              <Trans>DEMO</Trans>
            </Text>
          </View>
        </Layout.Header.Slot>
      </Layout.Header.Outer>

      <View style={[a.flex_1]}>
        <View
          style={[
            a.mx_lg,
            a.mt_md,
            a.p_md,
            a.rounded_lg,
            a.gap_xs,
            t.atoms.bg_contrast_25,
          ]}>
          <Text style={[a.text_sm, a.font_semi_bold]}>
            <Trans>Demo conversation</Trans>
          </Text>
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            {isGroup ? (
              <Trans>
                A simulated family group with {peer.displayName} and{' '}
                {DEMO_GROUP_MEMBER.displayName} — the invitation is shared with
                everyone.
              </Trans>
            ) : demo.role === 'learner' ? (
              <Trans>
                {peer.displayName} is a simulated parent. Accept her invitation
                below to complete the demo.
              </Trans>
            ) : (
              <Trans>
                {peer.displayName} is a simulated child. Accept his invitation
                below to complete the demo.
              </Trans>
            )}
          </Text>
          {demo.accepted ? (
            <Text style={[a.text_sm, {color: t.palette.positive_600}]}>
              <Trans>
                You and {peer.displayName} are now connected on Taderok 💛
              </Trans>
            </Text>
          ) : (
            <>
              {demo.declinedReason ? (
                <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                  <Trans>
                    You declined {peer.displayName}'s invitation — they
                    received your reply.
                  </Trans>
                </Text>
              ) : null}
              {demo.peerDeclinedCause ? (
                <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                  <Trans>
                    {peer.displayName} couldn't accept your invitation
                    ({demo.peerDeclinedCause}).
                  </Trans>
                </Text>
              ) : null}
              {demo.sentToChat && demo.code && !demo.peerDeclinedCause ? (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={_(
                    msg`Simulate ${peer.displayName} declining your invitation`,
                  )}
                  accessibilityHint={_(
                    msg`Shows how a polite decline reply appears in the chat`,
                  )}
                  onPress={() =>
                    setPeerDeclined(FAMILY_DECLINE_CAUSES[0])
                  }>
                  <Text
                    style={[
                      a.text_sm,
                      a.font_medium,
                      {color: t.palette.primary_700},
                    ]}>
                    <Trans>
                      Simulate {peer.displayName} declining your invitation
                    </Trans>
                  </Text>
                </Pressable>
              ) : null}
              <Text
                style={[
                  a.text_sm,
                  a.font_medium,
                  {color: t.palette.primary_700},
                ]}
                suppressHighlighting
                onPress={() => resetDemoScenario()}>
                <Trans>Reset demo scenario</Trans>
              </Text>
            </>
          )}
        </View>

        <FlatList
          data={items}
          keyExtractor={item => item.key}
          renderItem={({item}) => <DemoMessageView message={item.message} />}
          contentContainerStyle={[a.pb_5xl, a.pt_sm]}
          style={[a.flex_1]}
        />
      </View>
    </Layout.Screen>
  )
}

/**
 * A bubble that visually matches the real 1:1 chat: self messages are a solid
 * primary bubble aligned right, peer messages are a contrast bubble aligned
 * left, and a family-invite link in the text renders the real
 * MessageFamilyInvite embed underneath.
 */
function DemoMessageView({message}: {message: ChatBskyConvoDefs.MessageView}) {
  const t = useTheme()
  const {currentAccount} = useSession()

  const isFromSelf =
    currentAccount != null && message.sender?.did === currentAccount.did

  const rt = useMemo(
    () => new RichTextAPI({text: message.text, facets: message.facets}),
    [message.text, message.facets],
  )
  const familyInviteCode = familyInviteCodeFromFacets(message.facets)

  // Family invites render as ONE card — the sender's words, the invite cover
  // and the action — via the exact same component the real chat uses.
  if (familyInviteCode) {
    return (
      <View
        style={[
          a.px_lg,
          a.my_2xs,
          a.w_full,
          isFromSelf ? a.align_end : a.align_start,
        ]}>
        <MessageFamilyInvite
          code={familyInviteCode}
          messageText={message.text}
          messageFacets={message.facets}
          isFromSelf={isFromSelf}
        />
      </View>
    )
  }

  return (
    <View
      style={[
        a.px_lg,
        a.my_2xs,
        a.w_full,
        isFromSelf ? a.align_end : a.align_start,
      ]}>
      <View
        style={[
          a.rounded_xl,
          a.py_sm,
          a.px_md,
          {
            borderRadius: 18,
            maxWidth: '85%',
            backgroundColor: isFromSelf
              ? t.palette.primary_500
              : t.palette.contrast_50,
          },
        ]}>
        {rt.text.length > 0 ? (
          <RichText
            value={rt}
            style={[
              a.text_md,
              isFromSelf && {color: t.palette.white},
              !isFromSelf && t.atoms.text_contrast_high,
            ]}
            interactiveStyle={a.underline}
            enableTags
            shouldProxyLinks={true}
          />
        ) : null}
      </View>
    </View>
  )
}

function buildInviteMessage(
  text: string,
  senderDid: string,
  id: string,
  sentAt: string,
): ChatBskyConvoDefs.MessageView {
  const rt = new RichTextAPI({text})
  // Detect the link facet with exact byte offsets so the bubble renders the
  // link the same way a real message would.
  rt.detectFacetsWithoutResolution()
  return {
    $type: 'chat.bsky.convo.defs#messageView',
    id,
    rev: `rev-${id}`,
    text,
    facets: rt.facets ?? undefined,
    sender: {did: senderDid},
    sentAt,
  }
}
